/**
 * Locations Hook
 * Handles fetching locations with proper error handling
 */

import { useLogger } from '@/lib/logger';
import { useOrganization } from '@/contexts/OrganizationContext';
import * as Sentry from '@sentry/react';
import { trpc } from '@/lib/trpc';
import type { Location } from '../types';
import { excludeDelegatedLocations } from '../utils/delegatedLocations';
import { useEffect, useMemo } from 'react';

interface UseLocationsOptions {
  includeInactive?: boolean;
}

export function useLocations(options: UseLocationsOptions = {}) {
  const logger = useLogger();
  const { organization } = useOrganization();
  const includeInactive = options.includeInactive ?? false;

  const query = trpc.locations.list.useQuery(
    { includeInactive, organizationId: organization?.id },
    {
      enabled: !!organization?.id,
      retry: 2,
      retryDelay: 1000,
      staleTime: 30_000,
    }
  );

  useEffect(() => {
    if (query.data) {
      logger.info('Locations fetched successfully', {
        operation: 'fetch_locations',
        organizationId: organization?.id,
        count: query.data.length,
        includeInactive,
      });
    }

    if (query.error) {
      Sentry.captureException(query.error, {
        tags: {
          feature: 'locations',
          operation: 'fetch_locations',
        },
      });

      logger.error('Failed to fetch locations', {
        operation: 'fetch_locations',
        error: query.error.message,
        includeInactive,
      });
    }
  }, [query.data, query.error, logger, organization?.id, includeInactive]);

  /**
   * EVERY location the caller can reach, delegated ("shadow") rows included.
   *
   * This is the list to resolve a NAME against. A delegated location is still a
   * real place with real history: purchase orders, transfers, shipments, stock
   * movements and log rows all point at it, and resolving those ids against the
   * selectable list below renders "Unknown location" on records that are
   * perfectly well known (BLU-879 review). Naming is not choosing.
   *
   * Memoised for the same reason as `locations`.
   */
  const allLocations = useMemo(() => (query.data ?? []) as Location[], [query.data]);

  /**
   * The SELECTABLE list: delegated rows dropped. Nothing is picked, printed or
   * held back at a shadow row and the inventory push worker already skips it,
   * so offering one in a pick-list invites an operator to configure something
   * that can never come true.
   *
   * MEMOISED, not a bare `.filter()`. The old `(query.data ?? []) as Location[]`
   * returned `query.data` itself and was therefore reference-stable across
   * renders; several of the fourteen consumers (OrderCreate, Inventory,
   * TransferCreate, OrdersList) hold this array in effect dependencies, so a
   * fresh identity every render would loop them. `excludeDelegatedLocations`
   * preserves the reference when nothing is delegated, and the memo covers the
   * case where something is.
   *
   * Two SANCTIONED exceptions read `allLocations` instead of this one:
   * Settings > Locations (where the delegation is set, displayed and
   * re-targeted) and the stock-WRITE pickers, where ~200 live `sku_inventory`
   * rows sit at delegated locations and must stay adjustable and transferable
   * out (see `StockWriteLocationItems`).
   */
  const locations = useMemo(() => excludeDelegatedLocations(allLocations), [allLocations]);

  return {
    locations,
    allLocations,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
